import { BLOG_POSTS } from "@/lib/mock/blog";

/**
 * The people credited on Aqua MAG.
 *
 * This exists because the same bios were written out twice — inline in
 * blog.index.tsx and again in a local map inside blog.$category.$slug.tsx —
 * and the copies had already drifted.
 *
 * The article counts had drifted further still: both writer cards were
 * hardcoded to "9 Articles Published" when the real split is 15 and 6. Counts
 * are derived from BLOG_POSTS now, so publishing a post updates the card.
 */
export interface Author {
  /** Matches BlogPost.authorInitials for anyone who has published. */
  initials: string;
  name: string;
  bio: string;
  /**
   * Public professional profile, where the person has one.
   *
   * docs/project/content-seo-plan.md P1.5 asks for a stable author URL. A profile the
   * person controls is a real identifier — it lets Google resolve the byline
   * to a person who exists elsewhere, which an inline name cannot do. Emitted
   * as Person.url on the article, and only where a URL is actually known.
   */
  profileUrl?: string;
  /**
   * True for contributors who review rather than write. They have no byline,
   * so their card is expected to show zero articles — that is a role, not a
   * gap, and the card labels it as such.
   */
  advisory?: boolean;
}

export const AUTHORS: Author[] = [
  {
    initials: "VA",
    name: "Vahid A.",
    bio: "Holds a Master's degree in Biotechnology and brings 30 years of experience in agriculture, bioengineering, and aquaculture.",
  },
  {
    initials: "RR",
    name: "Reza R.",
    bio: "Project Manager with an extensive background in the tech industry, overseeing cross-functional delivery and international coordination.",
  },
];

const BY_INITIALS = new Map(AUTHORS.map((author) => [author.initials, author]));

/**
 * The bio for a byline, or undefined when the author is not on the list.
 *
 * Undefined rather than a default: the previous lookup fell back to Reza's
 * bio for any unrecognised initials, so a new writer would have silently been
 * described as someone else.
 */
export function authorBio(initials: string): string | undefined {
  return BY_INITIALS.get(initials)?.bio;
}

/** Public profile for a byline, or undefined when none is on file. */
export function authorProfileUrl(initials: string): string | undefined {
  return BY_INITIALS.get(initials)?.profileUrl;
}

/** Published article count per author, keyed by initials. */
export function articleCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const author of AUTHORS) counts[author.initials] = 0;
  for (const post of BLOG_POSTS) {
    counts[post.authorInitials] = (counts[post.authorInitials] ?? 0) + 1;
  }
  return counts;
}
